/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Company: SPHMedia
 * Description: Default Section Layout
 */

import { ReactElement, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Await } from "react-router-dom";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAd from "@components/Advertisement/PrestitialAd";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Header from "@components/Header/Header";
import Container from "@src/app/components/Container/Container";
import CustomError from "@src/app/components/Error/CustomError";
import FooterSubscribeRow from "@src/app/components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import GAData from "@src/app/components/GAData/GAData";
import Column from "@src/app/components/Grid/Column";
import Row from "@src/app/components/Grid/Row";
import Spinner from "@src/app/components/Spinner/Spinner";
import { AdvertisementTypeEnum } from "@src/app/types/enums";
import { WealthContext } from "@src/app/types/Page";
import { useContext } from "@src/app/util/getContext";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { sectionNavigationItems } from "@util/constant";
import { GoogleAdsSlotFactory } from "@util/helpers";

import TodaysMarketData from "./components/TodaysMarketData";
import WealthRealEstate from "./components/WealthRealEstate";
import WealthThirdRow from "./components/WealthThirdRow";
import WealthTopStories from "./components/WealthTopStories";
import WealthWatches from "./components/WealthWatches";

export default function WealthPage({
  testId,
}: {
  testId?: string;
}): ReactElement {
  const context: WealthContext = useContext();
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  return (
    <Suspense fallback={<Spinner />}>
      <Await resolve={context} errorElement={<CustomError />}>
        {({
          kind,
          error,
          data: { title, wealth, property, invest, advisor, asset, watches },
        }) => {
          return kind !== "section" || error ? (
            <h1>Error</h1>
          ) : (
            <>
              <Helmet>
                <title>{title}</title>
              </Helmet>

              <GAData
                level2={"wealth"}
                title={"Wealth_Index"}
                adblocker={
                  typeof window !== "undefined" ? 0 : (window as any).canRunAds
                }
              />

              <Advertisement
                adUnitProps={{
                  type: AdvertisementTypeEnum.LB1,
                  slot: GoogleAdsSlotFactory.lb1("wealth"),
                }}
              />

              {OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER ? (
                <PrestitialAd
                  slot={GoogleAdsSlotFactory.prestitial("wealth")}
                />
              ) : null}

              <Header mastheadTitle={"Wealth"} />

              {sectionNavigationItems["wealth"] ? (
                <div className="w-full bg-gray-250">
                  <Breadcrumb
                    rootClassName="m-auto container px-3"
                    parentCategory={"wealth"}
                    categoryDataMapping={sectionNavigationItems}
                  />
                </div>
              ) : null}

              <Container rootClassName="pt-6" data-testid={testId}>
                <Row rootClassName="mb-6">
                  <Column rootClassName="w-full px-3 lg:w-8/12">
                    <WealthTopStories data={wealth} />
                  </Column>

                  <Column rootClassName="w-full px-3 lg:w-4/12">
                    <Advertisement
                      rootClassName="bg-stripes mb-5 py-5"
                      adUnitProps={{
                        type: AdvertisementTypeEnum.IMU1,
                        slot: GoogleAdsSlotFactory.imu1("wealth"),
                      }}
                    />

                    <TodaysMarketData />
                  </Column>
                </Row>

                <>
                  {property ? (
                    <Row rootClassName="mb-6">
                      <Column rootClassName="w-full">
                        <WealthRealEstate data={property} />
                      </Column>
                    </Row>
                  ) : null}
                </>

                <>
                  {invest && advisor && asset ? (
                    <Row rootClassName="mb-6">
                      <Column rootClassName="w-full">
                        <WealthThirdRow
                          invest={invest}
                          advisor={advisor}
                          asset={asset}
                        />
                      </Column>
                    </Row>
                  ) : null}
                </>

                <>
                  {watches ? (
                    <Row rootClassName="mb-6">
                      <Column rootClassName="w-full">
                        <WealthWatches articles={watches} />
                      </Column>
                    </Row>
                  ) : null}
                </>
              </Container>

              <FooterSubscribeRow />
            </>
          );
        }}
      </Await>
    </Suspense>
  );
}
